<template>
  <j-abm
    name="office"
    :suppliers="suppliers"
    :features="{
      create: false,
      update: true,
      delete: false,
    }"
    @update="onUpdate">
    <template #form="{ item }">
      <div class="col-xl-5 col-lg-12 mb-2">
        <h5>{{ $t('general') }}</h5>
      </div>
      <div class="col-xl-7 col-lg-12">
        <a-form-model ref="general" :model="item" :rules="rules">
          <j-input prop="code" v-model="item.code" icon="number" :label="$t('code')" read-only />
          <j-input prop="external_code" v-model="item.external_code" icon="barcode" :label="$t('externalCode')" read-only />
          <j-input prop="name" v-model="item.name" icon="home" :label="$t('name')" read-only />
          <j-input prop="company" v-model="item.company" icon="bank" :label="$t('company')" read-only />
          <j-field prop="officeActivation" :label="$t('activation')">
            <a-select v-model="item.activation" size="large">
              <a-select-option v-for="activation in activationOptions" :key="activation" :value="activation">
                {{ $t(`enum.activation.${activation}`) }}
              </a-select-option>
            </a-select>
          </j-field>
          <j-field prop="skip_sync" :label="$t('skipSync')">
            <a-alert class="mt-2" type="info" show-icon :message="$t('skipSyncHelp')" />
            <div>
              <a-switch v-model="skip_sync" />
            </div>
          </j-field>
          <j-field prop="services" :label="$t('services')">
            <a-select v-model="item.services" mode="multiple" size="large" allow-clear>
              <a-select-option v-for="service in serviceOptions" :key="service" :value="service">
                {{ service }}
              </a-select-option>
            </a-select>
          </j-field>
          <j-input prop="description" v-model="item.description" icon="file-text" :label="$t('description')" />
        </a-form-model>
      </div>

      <a-divider />

      <div class="col-xl-5 col-lg-12 mb-2">
        <h5>{{ $t('address') }}</h5>
      </div>
      <div class="col-xl-7 col-lg-12">
        <a-form-model ref="address" :model="item.address" :rules="addressRules">
          <j-input prop="street" v-model="item.address.street" icon="environment" :label="$t('street')" />
          <j-input prop="apartment" v-model="item.address.apartment" icon="home" :label="$t('apartment')" />
          <j-input prop="locality" v-model="item.address.locality" icon="build" :label="$t('locality')" />
          <j-input prop="city" v-model="item.address.city" icon="flag" :label="$t('city')" />
          <j-input prop="state" v-model="item.address.state" icon="shop" :label="$t('state')" />
          <j-field prop="zip" :label="$t('zip')">
            <a-input v-model="zipCode" size="large" allow-clear />
          </j-field>
          <j-field prop="country" :label="$t('country')">
            <a-input value="AR" size="large" read-only />
          </j-field>
        </a-form-model>
      </div>

      <a-divider />

      <div class="col-xl-5 col-lg-12 mb-2">
        <h5>{{ $t('schedule') }}</h5>
      </div>
      <div class="col-xl-7 col-lg-12">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <strong>{{ $t('schedule') }}</strong>
          <a-button size="small" icon="plus" @click="addRange(item)">
            {{ $t('addRange') }}
          </a-button>
        </div>
        <div v-if="!Array.isArray(item.schedule) || item.schedule.length === 0" class="text-secondary mb-2">
          {{ $t('closed') }}
        </div>
        <div v-for="(range, idx) in item.schedule" :key="`range-${idx}`" class="d-flex align-items-center mb-2" style="gap: 8px;">
          <a-select v-model="range.day" size="large" style="width: 180px" @change="sortSchedule(item)">
            <a-select-option v-for="day in days" :key="day.value" :value="day.value">
              {{ $t(day.label) }}
            </a-select-option>
          </a-select>
          <a-input v-model="range.min_hour" type="time" size="large" style="width: 160px" />
          <span>-</span>
          <a-input v-model="range.max_hour" type="time" size="large" style="width: 160px" />
          <a-button icon="minus" shape="circle" size="small" @click="removeRange(item, idx)" />
        </div>
        <div v-if="scheduleError" class="text-danger">
          {{ scheduleError }}
        </div>
      </div>
    </template>
  </j-abm>
</template>

<script>
import lms from '@/api/lms'
import v from '@/utils/validators'
import JAbm from '@/views/shared/abm/update'
import JInput from '@/views/shared/forms/input'
import JField from '@/views/shared/forms/field'

const DAYS = [
  { value: 'MONDAY', label: 'monday' },
  { value: 'TUESDAY', label: 'tuesday' },
  { value: 'WEDNESDAY', label: 'wednesday' },
  { value: 'THURSDAY', label: 'thursday' },
  { value: 'FRIDAY', label: 'friday' },
  { value: 'SATURDAY', label: 'saturday' },
  { value: 'SUNDAY', label: 'sunday' },
]

const DAY_ORDER = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
}

export default {
  components: {
    JAbm,
    JInput,
    JField,
  },
  data: function () {
    return {
      rules: {},
      addressRules: {
        street: [v.required],
        state: [v.required],
      },
      serviceOptions: ['PICK_UP', 'DROP_OFF', 'DELIVERY'],
      activationOptions: ['ACTIVE', 'INACTIVE'],
      days: DAYS,
      scheduleError: '',
      zipCode: '',
      skip_sync: false,
    }
  },
  computed: {
    suppliers() {
      return {
        fetchOne: id => lms.office.fetchOne(id).then(res => {
          const office = res.data || {}
          office.id = office.id || office.office_id
          this.zipCode = office.address && office.address.zip ? String(office.address.zip) : ''
          this.skip_sync = typeof office.skip_sync === 'boolean' ? office.skip_sync : false
          office.schedule = this.normalizeScheduleFromApi(office.schedule || office.office_schedule)
          return res
        }),
        payload: () => ({
          id: null,
          code: '',
          external_code: '',
          name: '',
          company: '',
          activation: 'INACTIVE',
          services: [],
          description: null,
          skip_sync: false,
          address: {
            street: '',
            apartment: null,
            locality: null,
            city: '',
            state: '',
            country: 'AR',
            zip: null,
          },
          schedule: [],
        }),
      }
    },
  },
  methods: {
    normalizeScheduleFromApi(value) {
      const list = Array.isArray(value) ? value : []
      return list.map(range => ({
        day: range.day || 'MONDAY',
        min_hour: range.min_hour || '',
        max_hour: range.max_hour || '',
      })).sort(this.compareSchedule)
    },
    compareSchedule(a, b) {
      const dayA = DAY_ORDER[a.day] !== undefined ? DAY_ORDER[a.day] : 99
      const dayB = DAY_ORDER[b.day] !== undefined ? DAY_ORDER[b.day] : 99
      if (dayA !== dayB) {
        return dayA - dayB
      }
      return (a.min_hour || '').localeCompare(b.min_hour || '')
    },
    sortSchedule(item) {
      if (Array.isArray(item.schedule)) {
        item.schedule.sort(this.compareSchedule)
      }
    },
    scheduleByDay(day) {
      const form = this.$refs.general && this.$refs.general.model
      if (!form || !Array.isArray(form.schedule)) return []
      return form.schedule.filter(range => range.day === day)
    },
    addRange(item) {
      if (!Array.isArray(item.schedule)) item.schedule = []
      item.schedule.push({
        day: 'MONDAY',
        min_hour: '09:00',
        max_hour: '18:00',
      })
      this.sortSchedule(item)
      this.scheduleError = ''
    },
    removeRange(item, idx) {
      if (Array.isArray(item.schedule) && idx >= 0 && idx < item.schedule.length) {
        item.schedule.splice(idx, 1)
        this.sortSchedule(item)
      }
    },
    validateSchedule(schedule) {
      const list = Array.isArray(schedule) ? schedule : []

      const filledRanges = list.filter(range => range && (range.min_hour || range.max_hour))
      if (!filledRanges.length) {
        this.scheduleError = ''
        return true
      }

      const invalid = filledRanges.some(range => !range.min_hour || !range.max_hour || range.min_hour >= range.max_hour)
      if (invalid) {
        this.scheduleError = this.$t('msg.office.schedule.invalid')
        return false
      }

      this.scheduleError = ''
      return true
    },
    buildPayload(office) {
      const skipSyncValue = Boolean(this.skip_sync)
      const officeId = office.id || office.office_id || this.$route.params.office
      const schedulePayload = (office.schedule || [])
        .filter(range => range && range.min_hour && range.max_hour)
        .map(range => ({
          day: range.day,
          min_hour: range.min_hour,
          max_hour: range.max_hour,
        }))

      const payload = {
        id: officeId,
        code: office.code,
        external_code: office.external_code || null,
        name: office.name,
        company: office.company,
        activation: office.activation,
        skip_sync: skipSyncValue,
        services: Array.isArray(office.services) ? office.services : [],
        description: office.description,
        address: {
          ...office.address,
          country: 'AR',
          type: 'BUSINESS',
          zip: this.zipCode && this.zipCode.trim() ? this.zipCode.trim() : null,
        },
        schedule: schedulePayload,
      }

      return payload
    },
    onUpdate(office) {
      Promise.all([
        new Promise(resolve => this.$refs.address.validate(resolve)),
      ]).then(results => {
        const validSchedule = this.validateSchedule(office.schedule)
        if (results.every(Boolean) && validSchedule) {
          const payload = this.buildPayload(office)
          console.log('office.update.payload', payload)
          lms.office.update(payload)
            .then(() => this.$router.push('/offices'))
            .catch(error => {
              console.error('office.update.error', {
                status: error && error.response ? error.response.status : null,
                data: error && error.response ? error.response.data : null,
                message: error ? error.message : null,
              })
              const message = error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : this.$t('retry')
              this.$notification.error({
                message: this.$t('error'),
                description: message,
              })
            })
        }
      })
    },
  },
}
</script>
